// frontend/src/components/formularios/ListaFormulariosConDependencias.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  Card,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Alert,
  CircularProgress,
  Badge,
  Menu,
  MenuItem
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Assignment as AssignmentIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Timeline as TimelineIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import formularioService from '../../services/formularioService';
import { useAuth } from '../../hooks/useAuth';

const ListaFormulariosConDependencias = ({ 
  historiaId, 
  especialidadId,
  pacienteId 
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [formulariosPadre, setFormulariosPadre] = useState([]);
  const [formulariosHijos, setFormulariosHijos] = useState({});
  const [expandidos, setExpandidos] = useState({});
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [formularioSeleccionado, setFormularioSeleccionado] = useState(null);
  
  useEffect(() => {
    cargarFormularios();
  }, [historiaId, especialidadId]);
  
  const cargarFormularios = async () => {
    try {
      setLoading(true);
      
      // Obtener todas las respuestas de formularios
      const respuestas = await formularioService.getRespuestasPorHistoriaEspecialidad(historiaId);
      
      if (!respuestas.data || !Array.isArray(respuestas.data)) {
        console.error('No se recibieron respuestas válidas');
        return;
      }
      
      // Separar formularios padre e hijos
      const padres = [];
      const hijos = {};
      
      respuestas.data.forEach(respuesta => {
        if (respuesta.formulario?.tipo_dependencia === 'padre') {
          padres.push(respuesta);
          hijos[respuesta.id] = [];
        }
      });
      
      // Asociar hijos con sus padres
      respuestas.data.forEach(respuesta => {
        if (respuesta.parent_respuesta_id && hijos[respuesta.parent_respuesta_id]) {
          hijos[respuesta.parent_respuesta_id].push(respuesta);
        }
      });
      
      setFormulariosPadre(padres);
      setFormulariosHijos(hijos);
      
      // Expandir automáticamente si hay pocos formularios
      if (padres.length <= 3) {
        const expandidosIniciales = {};
        padres.forEach(p => {
          expandidosIniciales[p.id] = true;
        });
        setExpandidos(expandidosIniciales);
      }
      
    } catch (error) {
      console.error('Error al cargar formularios:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleExpandir = (padreId) => {
    setExpandidos(prev => ({
      ...prev,
      [padreId]: !prev[padreId]
    }));
  };
  
  const handleCrearSeguimiento = async (formularioPadre) => {
    try {
      // Verificar que el padre esté cerrado
      if (formularioPadre.estado !== 'cerrado') {
        alert('Debe cerrar el plan terapéutico antes de crear seguimientos');
        return;
      }
      
      // Obtener formularios hijos disponibles
      const hijosDisponibles = await formularioService.getFormulariosHijos(formularioPadre.id);
      
      if (!hijosDisponibles.success || !hijosDisponibles.data?.formulariosHijos?.length) {
        alert('No hay formularios de seguimiento disponibles');
        return;
      }
      
      // Si hay múltiples tipos de seguimiento, mostrar selector
      if (hijosDisponibles.data.formulariosHijos.length > 1) {
        // Por ahora, usar el primero
        const formularioHijo = hijosDisponibles.data.formulariosHijos[0];
        navigate(`/historias/${historiaId}/formulario/${formularioHijo.id}?parent=${formularioPadre.id}`);
      } else {
        const formularioHijo = hijosDisponibles.data.formulariosHijos[0];
        navigate(`/historias/${historiaId}/formulario/${formularioHijo.id}?parent=${formularioPadre.id}`);
      }
    } catch (error) {
      console.error('Error al crear seguimiento:', error);
      alert('Error al crear seguimiento');
    }
  };
  
  const handleVerFormulario = (respuestaId) => {
    navigate(`/formularios/respuesta/${respuestaId}/ver`);
  };
  
  const handleEditarFormulario = (respuesta) => {
    if (respuesta.estado === 'cerrado') {
      alert('No se puede editar un formulario cerrado');
      return;
    }
    navigate(`/formularios/${respuesta.formulario_id}/editar/${respuesta.id}`);
  };
  
  const handleMenuClick = (event, formulario) => {
    setMenuAnchor(event.currentTarget);
    setFormularioSeleccionado(formulario);
  };
  
  const handleMenuClose = () => {
    setMenuAnchor(null);
    setFormularioSeleccionado(null);
  };
  
  const handleReabrirFormulario = async () => {
    if (!formularioSeleccionado || user?.rol_id !== 1) {
      alert('Solo los administradores pueden reabrir formularios');
      handleMenuClose();
      return;
    }
    
    try {
      await formularioService.reabrirFormularioPadre(
        formularioSeleccionado.id,
        'Reabierto por administrador'
      );
      
      await cargarFormularios();
      handleMenuClose();
    } catch (error) {
      console.error('Error al reabrir formulario:', error);
      alert('Error al reabrir el formulario');
    }
  };
  
  const handleVerProgreso = async (formularioPadre) => {
    try {
      const analisis = await formularioService.getAnalisisProgreso(formularioPadre.id);
      
      if (analisis.success) {
        navigate(`/formularios/progreso/${formularioPadre.id}`);
      }
    } catch (error) {
      console.error('Error al obtener progreso:', error);
    }
  };
  
  const getEstadoChip = (estado, bloqueadoPor) => {
    if (bloqueadoPor) {
      return <Chip icon={<LockIcon />} label="Bloqueado" color="error" size="small" />;
    }
    
    switch (estado) {
      case 'cerrado':
        return <Chip icon={<LockIcon />} label="Cerrado" color="default" size="small" />;
      case 'abierto':
        return <Chip icon={<LockOpenIcon />} label="Abierto" color="success" size="small" />;
      default:
        return <Chip label={estado} size="small" />;
    }
  };
  
  const calcularProgresoHijos = (hijos) => {
    if (!hijos || hijos.length === 0) return null;
    
    const total = hijos.length;
    const completados = hijos.filter(h => 
      h.respuestas?.estado_progreso === 'logrado' ||
      h.respuestas?.objetivos_mensuales && 
      Object.values(h.respuestas.objetivos_mensuales).some(o => o.observacion === 'Logrado')
    ).length;
    
    return {
      total,
      completados,
      porcentaje: Math.round((completados / total) * 100)
    };
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (formulariosPadre.length === 0) {
    return (
      <Alert severity="info">
        No hay formularios completados para esta historia clínica.
      </Alert>
    );
  }
  
  return (
    <Box>
      {formulariosPadre.map((formularioPadre) => {
        const hijos = formulariosHijos[formularioPadre.id] || [];
        const progreso = calcularProgresoHijos(hijos);
        const puedeCrearHijos = formularioPadre.estado === 'cerrado' && 
                               formularioPadre.formulario?.permite_dependencias;
        
        return (
          <Accordion
            key={formularioPadre.id}
            expanded={expandidos[formularioPadre.id] || false}
            onChange={() => handleExpandir(formularioPadre.id)}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                <Box display="flex" alignItems="center" gap={2}>
                  <AssignmentIcon color="primary" />
                  <Box>
                    <Typography variant="h6">
                      {formularioPadre.formulario?.nombre}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Completado: {new Date(formularioPadre.fecha_completado).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>
                
                <Box display="flex" alignItems="center" gap={2} onClick={(e) => e.stopPropagation()}>
                  {progreso && (
                    <Badge 
                      badgeContent={`${progreso.completados}/${progreso.total}`} 
                      color={progreso.porcentaje >= 70 ? "success" : "warning"}
                    >
                      <TimelineIcon />
                    </Badge>
                  )}
                  
                  {getEstadoChip(formularioPadre.estado, formularioPadre.bloqueado_por)}
                  
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuClick(e, formularioPadre)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Box>
              </Box>
            </AccordionSummary>
            
            <AccordionDetails>
              <Box>
                {/* Información del formulario padre */}
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="textSecondary">
                          Especialista
                        </Typography>
                        <Typography variant="body1">
                          {formularioPadre.especialista?.usuario?.nombre} {formularioPadre.especialista?.usuario?.apellidos}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="textSecondary">
                          Estado del Plan
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                          {formularioPadre.estado === 'cerrado' ? (
                            <CheckCircleIcon color="success" fontSize="small" />
                          ) : (
                            <WarningIcon color="warning" fontSize="small" />
                          )}
                          <Typography variant="body1">
                            {formularioPadre.estado === 'cerrado' ? 'Plan Completado' : 'Plan en Proceso'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                  
                  <CardActions>
                    <Button
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleVerFormulario(formularioPadre.id)}
                    >
                      Ver Plan
                    </Button>
                    
                    {puedeCrearHijos && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleCrearSeguimiento(formularioPadre)}
                      >
                        Nuevo Seguimiento
                      </Button>
                    )}
                    
                    {hijos.length > 0 && (
                      <Button
                        size="small"
                        startIcon={<TimelineIcon />}
                        onClick={() => handleVerProgreso(formularioPadre)}
                      >
                        Ver Progreso
                      </Button>
                    )}
                  </CardActions>
                </Card>
                
                {/* Lista de formularios hijos */}
                {hijos.length > 0 ? (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Seguimientos ({hijos.length})
                    </Typography>
                    <List dense>
                      {hijos.map((hijo, index) => (
                        <React.Fragment key={hijo.id}>
                          <ListItem>
                            <ListItemText
                              primary={`Sesión ${index + 1} - ${new Date(hijo.fecha_completado).toLocaleDateString()}`}
                              secondary={
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Typography variant="caption">
                                    {hijo.formulario?.nombre}
                                  </Typography>
                                  {hijo.bloqueado_por && (
                                    <Chip 
                                      icon={<LockIcon />} 
                                      label="Bloqueado" 
                                      size="small" 
                                      color="error"
                                    />
                                  )}
                                </Box>
                              }
                            />
                            <ListItemSecondaryAction>
                              <Tooltip title="Ver seguimiento">
                                <IconButton
                                  edge="end"
                                  size="small"
                                  onClick={() => handleVerFormulario(hijo.id)}
                                >
                                  <VisibilityIcon />
                                </IconButton>
                              </Tooltip>
                            </ListItemSecondaryAction>
                          </ListItem>
                          {index < hijos.length - 1 && <Divider variant="inset" component="li" />}
                        </React.Fragment>
                      ))}
                    </List>
                  </Box>
                ) : (
                  <Alert severity="info" icon={<InfoIcon />}>
                    {formularioPadre.estado === 'cerrado' ? 
                      'No hay seguimientos registrados. Puede crear uno nuevo.' :
                      'Debe cerrar el plan terapéutico para poder crear seguimientos.'
                    }
                  </Alert>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
      
      {/* Menú contextual */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          handleVerFormulario(formularioSeleccionado?.id);
          handleMenuClose();
        }}>
          <ListItemText primary="Ver formulario" />
        </MenuItem>
        
        {formularioSeleccionado?.estado === 'abierto' && (
          <MenuItem onClick={() => {
            handleEditarFormulario(formularioSeleccionado);
            handleMenuClose();
          }}>
            <ListItemText primary="Editar" />
          </MenuItem>
        )}
        
        {user?.rol_id === 1 && formularioSeleccionado?.estado === 'cerrado' && (
          <MenuItem onClick={handleReabrirFormulario}>
            <ListItemText primary="Reabrir formulario" />
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default ListaFormulariosConDependencias;